import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Dashboard.module.css';

interface PromoCode {
  id: string;
  code: string;
  discountPercent: number;
  adminCreated: boolean;
  active: boolean;
  usageCount: number;
  maxUsage: number | null;
  createdAt: string;
  affiliate: {
    id: string;
    email: string;
    name: string;
  } | null;
  _count: {
    commissions: number;
  };
}

const PromoCodes: React.FC = () => {
  const navigate = useNavigate();
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newDiscount, setNewDiscount] = useState('');
  const [newMaxUsage, setNewMaxUsage] = useState('');
  const [unlimitedUsage, setUnlimitedUsage] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
  }, [navigate]);

  const fetchPromoCodes = async () => {
    try {
      const res = await api.get('/admin/promo-codes');
      setPromoCodes(res.data.promoCodes);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const handleCreatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      await api.post('/admin/promo-codes', {
        code: newCode,
        discountPercent: parseFloat(newDiscount),
        maxUsage: unlimitedUsage ? null : parseInt(newMaxUsage) || null,
      });
      setNewCode('');
      setNewDiscount('');
      setNewMaxUsage('');
      setUnlimitedUsage(true);
      setShowCreateModal(false);
      fetchPromoCodes();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create promo code');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggleActive = async (id: string, active: boolean) => {
    try {
      await api.put(`/admin/promo-codes/${id}`, { active: !active });
      fetchPromoCodes();
    } catch (error) {
      console.error('Failed to toggle promo code:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;

    try {
      await api.delete(`/admin/promo-codes/${id}`);
      fetchPromoCodes();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete promo code');
    }
  };

  return (
    <AdminLayout>
      <div className={styles.contentHeader}>
        <div>
          <h1>Promo Codes</h1>
          <p>Manage promotional codes</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setShowCreateModal(true)}>
          <FiPlus /> Create Admin Code
        </button>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Type</th>
                  <th>Created By</th>
                  <th>Usage</th>
                  <th>Sales</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map((code) => (
                  <tr key={code.id}>
                    <td><strong>{code.code}</strong></td>
                    <td>{code.discountPercent}%</td>
                    <td>
                      <span className={code.adminCreated ? styles.statusPaid : styles.statusReady}>
                        {code.adminCreated ? 'Admin' : 'Affiliate'}
                      </span>
                    </td>
                    <td>
                      {code.affiliate ? (
                        <span>{code.affiliate.name} ({code.affiliate.email})</span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>Admin</span>
                      )}
                    </td>
                    <td>
                      {code.usageCount} / {code.maxUsage !== null ? code.maxUsage : '∞'}
                    </td>
                    <td>{code._count.commissions}</td>
                    <td>
                      <span className={code.active ? styles.statusPaid : styles.statusCancelled}>
                        {code.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(code.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleToggleActive(code.id, code.active)}
                        >
                          {code.active ? 'Deactivate' : 'Activate'}
                        </button>
                        {code.usageCount === 0 && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleDelete(code.id)}
                            style={{ background: '#ef4444' }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {promoCodes.length === 0 && (
              <div className={styles.emptyState}>
                <p>No promo codes found</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className={styles.modal} onClick={() => setShowCreateModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Create Admin Promo Code</h3>
            <form onSubmit={handleCreatePromoCode}>
              <div className={styles.formGroup}>
                <label>Code (6-20 alphanumeric)</label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="SUMMER2026"
                  required
                  minLength={6}
                  maxLength={20}
                  pattern="[A-Z0-9]+"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Discount Percent (1-100)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="100"
                  value={newDiscount}
                  onChange={(e) => setNewDiscount(e.target.value)}
                  placeholder="10"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={unlimitedUsage}
                    onChange={(e) => setUnlimitedUsage(e.target.checked)}
                  />
                  Unlimited usage
                </label>
              </div>
              {!unlimitedUsage && (
                <div className={styles.formGroup}>
                  <label>Max Usage</label>
                  <input
                    type="number"
                    min="1"
                    value={newMaxUsage}
                    onChange={(e) => setNewMaxUsage(e.target.value)}
                    placeholder="100"
                    required={!unlimitedUsage}
                  />
                </div>
              )}
              <div className={styles.modalActions}>
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={createLoading}>
                  {createLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PromoCodes;
